
// Utility function to compare version differences
export const getObjectDiff = (obj1: any, obj2: any, path = '', visitedPairs = new WeakMap()) => {
  // Handle null or undefined inputs
  if (!obj1 && !obj2) return [];
  if (!obj1) return [{ path: path || 'root', oldValue: undefined, newValue: obj2 }];
  if (!obj2) return [{ path: path || 'root', oldValue: obj1, newValue: undefined }];
  
  // Check for circular references
  if (typeof obj1 === 'object' && typeof obj2 === 'object' && obj1 !== null && obj2 !== null) {
    const pairKey = visitedPairs.get(obj1);
    if (pairKey === obj2) {
      return [];
    }
    visitedPairs.set(obj1, obj2);
  }
  
  // Only proceed if both are objects
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    if (obj1 !== obj2) {
      return [{ path: path || 'root', oldValue: obj1, newValue: obj2 }];
    }
    return [];
  }
  
  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
  const changes: { path: string; oldValue: any; newValue: any }[] = [];
  
  allKeys.forEach(key => {
    const currentPath = path ? `${path}.${key}` : key;
    
    // Skip system fields and version reference fields
    const skipFields = ['id', 'createdAt', 'lastEditedAt', 'publishedVersionId', 'latestVersionId', '_form', 'workflowStatus', 'forceHide'];
    if (skipFields.includes(key) || currentPath.includes('publishedVersionId') || currentPath.includes('latestVersionId')) {
      return;
    }
    
    if (!(key in obj1)) {
      changes.push({
        path: currentPath,
        oldValue: undefined,
        newValue: obj2[key]
      });
      return;
    }
    
    if (!(key in obj2)) {
      changes.push({
        path: currentPath,
        oldValue: obj1[key],
        newValue: undefined
      });
      return;
    }
    
    const val1 = obj1[key];
    const val2 = obj2[key];
    
    if (val1 === val2) return;
    
    // Handle Firestore references and other special objects
    if (typeof val1 === 'object' && typeof val2 === 'object' && val1 !== null && val2 !== null) {
      // Handle Firestore references
      if ('path' in val1 && 'id' in val1 && 'path' in val2 && 'id' in val2) {
        if (val1.id !== val2.id) {
          changes.push({
            path: currentPath,
            oldValue: val1,
            newValue: val2
          });
        }
        return;
      }
      
      // Add depth limit
      if (currentPath.split('.').length > 10) {
        if (JSON.stringify(val1) !== JSON.stringify(val2)) {
          changes.push({
            path: currentPath,
            oldValue: val1,
            newValue: val2
          });
        }
        return;
      }
      
      // Recursively compare nested objects
      const nestedChanges = getObjectDiff(val1, val2, currentPath, visitedPairs);
      changes.push(...nestedChanges);
    } else if (JSON.stringify(val1) !== JSON.stringify(val2)) {
      changes.push({
        path: currentPath,
        oldValue: val1,
        newValue: val2
      });
    }
  });
  
  return changes;
};
